import type { ComponentType, ReactNode, RefObject } from 'react';
import type { FormikInputProps } from 'Components/frameworks/formik/FormikInput';

export type PaymentFormTermsAndConditions = {
  cgv: boolean;
  donation?: boolean;
};

export type PaymentFormHealthRegulations = {
  cgv: boolean;
};

export type PaymentFormValues = {
  freeDeposit: number | '';
  amountToPay: number;
  paymentProviderId?: string;
  selectorPaymentProviderId?: string;
  paymentMethodId: string;
  paymentConditionId: string;
  paymentProduct?: string;
  termsAndConditions: PaymentFormTermsAndConditions;
  HealthRegulations: PaymentFormHealthRegulations;
  currentPayingCustomerId?: string;
  billingDetails?: BillingDetail;
  comments?: string;
  token?: string;
  card_holder?: string;
  year?: string;
  cvv?: string;
  number?: string;
  valid?: boolean;
  browserInfo?: string;
  paymentId?: string;
  selectedVoxpayMethod?: string;
};

export type PaymentBookingResumeAttendee = {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhoneNumber?: string;
  dateOfBirth?: string;
  birthday?: string;
  address?: {
    city?: string;
    countryCode?: string;
    stateOrDistrict?: string;
    streetName?: string;
    streetNumber?: string;
    zipCode?: string;
  };
};

export type PaymentBookingResume = {
  arrivalDate: APIDate;
  proposalId?: string;
  bookingId?: string;
  departureDate: APIDate;
  name: string;
  transports: Array<{
    company: {
      name: string;
      id: string;
    };
    origin: string;
    destination: string;
    departure: {
      apc: string;
      date: string;
    };
    arrival: {
      apc: string;
      date: string;
    };
  }>;
  accommodations: {
    count: number;
    name?: string;
  };
  attendees?: ReadonlyArray<PaymentBookingResumeAttendee>;
};

export type PaymentRedirectMutationParamsExtended = PaymentRedirectMutationParams & {
  callReference?: string;
  contactID?: string;
  device?: string;
};

export type PaymentRedirectResponse = {
  paymentRedirect: {
    orderId?: string | null;
    bookingId?: string;
    method?: string;
    url?: string;
    fields?: {
      pp?: string;
      [key: string]: unknown;
    };
  };
};

export type PaymentErrorMessageParams = {
  errorCode?: string;
  statusCode?: number;
  track: (arg0: Record<string, unknown>) => void;
  isCmta?: boolean;
};

export type UpliftBuildOrderParams = {
  price?: number;
  accommodations?: { count?: number; name?: string };
  departureDate?: string;
  arrivalDate?: string;
  name?: string;
  attendees?: ReadonlyArray<PaymentBookingResumeAttendee>;
  transports?: PaymentBookingResume['transports'];
};

export type PledgScheduleInstallment = {
  amount_cents: number;
  fees?: number;
  payment_date?: string;
};

export type HostedFieldValidationState = {
  valid: boolean;
  length: number;
  errorMessage?: string;
};

export type UpliftStatusType =
  | 'OFFER_AVAILABLE'
  | 'TOKEN_AVAILABLE'
  | 'TOKEN_RETRIEVED'
  | 'OFFER_UNAVAILABLE'
  | 'SERVICE_UNAVAILABLE';

export type UpliftChangeEvent = {
  status: UpliftStatusType;
  token?: {
    card_token: string;
  };
};

export type UpliftPaymentsApi = {
  init: (configuration: Record<string, unknown>) => void;
  load: (order: unknown) => void;
  select: () => void;
  deselect: () => void;
  exit: () => void;
  error: (errorMsg: string, severity: string) => void;
  confirm: (orderId: string) => void;
  getToken: () => void;
};

export type UpliftApi = {
  Analytics: {
    orderSubmit: (order: unknown, options: Record<string, unknown>) => void;
    orderResponse: (options: Record<string, unknown>) => void;
  };
  Payments: UpliftPaymentsApi;
};

export function getUpliftApi(): UpliftApi | undefined {
  return window.Uplift as UpliftApi | undefined;
}

export type PaymentJsPayment = {
  setNumberStyle: (style: Record<string, unknown>) => void;
  setCvvStyle: (style: Record<string, unknown>) => void;
  setNumberPlaceholder: (placeholder: string) => void;
  setCvvPlaceholder: (placeholder: string) => void;
  numberOn: (
    event: string,
    callback: (data: { validNumber: boolean; numberLength: number }) => void,
  ) => void;
  cvvOn: (
    event: string,
    callback: (data: { validCvv: boolean; cvvLength: number }) => void,
  ) => void;
};

export type PaymentJsInstance = {
  init: (
    key: string,
    numberId: string,
    cvvId: string,
    callback: (payment: PaymentJsPayment) => void,
  ) => void;
  tokenize: (
    data: { card_holder: string; month: string; year: string },
    success: (token: string) => void,
    error: (errors: ReadonlyArray<{ attribute: string; message: string }>) => void,
  ) => void;
};

export type HiPayPaymentData = {
  token: string;
  browser_info?: unknown;
  payment_product?: string;
};

export type HiPayCardInstance = {
  getPaymentData: () => Promise<HiPayPaymentData>;
  on: (event: string, callback: (...args: unknown[]) => void) => void;
};

export type HiPayClient = {
  create: (type: string, options: Record<string, unknown>) => HiPayCardInstance;
};

declare global {
  interface Window {
    PaymentJs?: new (version: string) => PaymentJsInstance;
    upReady?: () => void;
    HiPay?: (config: Record<string, unknown>) => HiPayClient;
    Pledg?: new (el: HTMLElement | null, config: Record<string, unknown>) => void;
    Uplift?: UpliftApi;
    Oney?: {
      loadSimulationPopin: (options: Record<string, unknown>) => void;
    };
    loadOneyWidget?: (callback: () => void) => void;
    oneyMerchantApp?: {
      loadSimulationPopin: (options: { options: Record<string, unknown> }) => void;
    };
  }
}

export type BookingNavbarContextExtended = {
  hideContinueButton?: boolean;
  setContinueAction?: ((action: () => void) => void) | null;
  setHideContinueButton?: ((value: boolean) => void) | null;
  setIsPriceLoading?: ((value: boolean) => void) | null;
  navbarRef?: RefObject<HTMLElement> | null;
  setNoEdit?: ((value: boolean) => void) | null;
  setOverwriteContinueButton?: ((value: boolean) => void) | null;
  setPortalPosition?: ((position: string) => void) | null;
  setIsDisabled?: ((value: boolean) => void) | null;
  setValidationCallback?: ((callback: () => void) => void) | null;
  validationCallback?: () => unknown;
  setProcessValidation?: ((value: boolean) => void) | null;
  setPaymentLabel?: ((label: string) => void) | null;
  setContinueBtn?: ((btn: ReactNode) => void) | null;
};

export type PlanSelectorComponent = ComponentType<{
  price?: number;
  label: string;
}>;

export type ExtendedFormikInputProps = Omit<
  FormikInputProps,
  'label' | 'labelAdditionalInfo' | 'type'
> & {
  label?: ReactNode;
  labelAdditionalInfo?: ReactNode;
  type?: FormikInputProps['type'] | string;
  onWheel?: () => void;
  placeholder?: string;
  title?: string;
  customClasses?: string;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
  dateFormat?: string;
  options?: ReadonlyArray<{ name: string; value: string }>;
  showValidateState?: boolean;
  value?: string;
  onChange?: (event: unknown) => void;
  ariaDescribedBy?: string;
  customId?: string;
  validate?: (value: unknown) => unknown;
};

export type CyberSourceFlexConstructor = new (token: string) => {
  microform: (options: Record<string, unknown>) => CyberSourceMicroform;
};

export type CyberSourceMicroform = {
  createField: (
    type: string,
    options: Record<string, unknown>,
  ) => {
    load: (selector: string) => void;
    on: (
      event: string,
      callback: (data: {
        valid: boolean;
        card?: ReadonlyArray<{ name?: string }>;
        empty: boolean;
      }) => void,
    ) => void;
  };
  createToken: (
    data: { expirationMonth: string; expirationYear: string },
    callback: (err: { message?: string } | null, tokenizedCard: string) => void,
  ) => void;
};

export type PlanIframeProps = {
  onLoaded: (display: boolean) => void;
  price: number;
  display: boolean;
  onTokenAvailable?: () => void;
  onTokenRetrieved?: (token: string) => void;
  booking?: UpliftBuildOrderParams;
  currency?: string;
  isReadyToDisplay?: boolean;
  shouldInit?: boolean;
  isPaymentPage?: boolean;
};

export type UpliftConfirmationProps = {
  booking: UpliftBuildOrderParams & { bookingId?: string };
  currency: string;
  paymentStatus?: string;
  shouldConfirm?: boolean;
};

export type B2BPaymentFormValues = PaymentFormValues & {
  paymentProviderId: string;
};

export type DelCredereFormValues = {
  termsAndConditions: PaymentFormTermsAndConditions;
};

export type TrackPayementFunction = (payload: {
  orderId: string | null;
  isErrored?: boolean;
  bookingId?: string;
  amountToPay: number;
  errorMsg?: string;
  paymentMethodLabel: string;
  paymentProviderId: string;
}) => void;

export function createMockPaymentProvider(
  overrides: Partial<PaymentProvider> & Pick<PaymentProvider, 'id' | 'selectorId'>,
): PaymentProvider {
  const { id, selectorId, ...rest } = overrides;
  if (Object.keys(rest).length === 0) {
    return { id } as PaymentProvider;
  }
  return { id, selectorId, ...rest } as PaymentProvider;
}

export function asPaymentProviders(
  providers: ReadonlyArray<Partial<PaymentProvider> & Pick<PaymentProvider, 'id' | 'selectorId'>>,
): ReadonlyArray<PaymentProvider> {
  return providers.map((provider) => createMockPaymentProvider(provider));
}

export const minimalAppContext = { locale: 'fr-FR' } as AppContext;

export const defaultGqlQueryOptions = { requestName: 'B2C|PAYMENT|GQL' };

export function toNumericPrice(price: Price | number | undefined): number {
  if (typeof price === 'number') {
    return price;
  }
  return price?.value ?? 0;
}

export function toSessionStorageValue(value: number | string): Record<string, unknown> {
  return { value } as Record<string, unknown>;
}
